import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DateTime, Duration } from 'luxon';
import { SbLocalDateTime } from 'src/app/services/api/api-date-time';
import {
  API,
  BookingDTO,
  BookingStatus,
  CampaignWithPreviewDTO,
  CommercialDTO,
  CommercialFullDTO,
  CullDTO,
  CullWithAtomsDTO,
  GeoCullDTO,
  InventoryGeoAggregationFrontendEnhancedDTO,
  MediumFullDTO,
  PlayerCullDTO,
  RequirementFullDTO,
  StateSpaceCullDTO,
  TimeCullDTO,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { BookingService } from '../../../../services/booking.service';
import { TranslateService } from '@ngx-translate/core';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { ClusterDefinition, MinMaxPoints } from '../../../../services/global/global-interfaces';
import { ToasterService, ToastType } from '../../../../services/toaster/toaster.service';
import { NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { PermissionService } from '../../../../services/permission.service';
import { CommercialReservationService } from '../../../../services/commercial-reservation.service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';
import { QueryParamService } from '../../../../services/lists/query-params';
import { iif, of } from 'rxjs';
import { NgbTranslatedDatepicker } from '../../../../services/date-time/ngb-datepicker-translated.service';

@Component({
  selector: 'sb-page-commercial-edit',
  templateUrl: './commercial-edit.component.html',
  styleUrls: ['./commercial-edit.component.scss'],
  providers: [{ provide: NgbDatepickerI18n, useClass: NgbTranslatedDatepicker }],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [style({ transform: 'translateX(+100%)' }), animate('200ms', style({ transform: 'translateX(0%)' }))]),
      transition(':leave', [animate('200ms', style({ transform: 'translateX(+100%)' }))]),
    ]),
  ],
  standalone: false,
})
export class CommercialEditComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('wizard') wizard: SbWizardComponent;
  @ViewChild('tab3') tab3: SbWizardTabComponent;
  isREWEClient: boolean;
  minMaxPoints: MinMaxPoints;
  clusters: ClusterDefinition[];
  form: UntypedFormGroup;
  clientId: number;
  campaignId: number;
  campaignData: CampaignWithPreviewDTO;
  commercialData: CommercialFullDTO;
  requirementData: RequirementFullDTO;
  useMotiveGroupForBookings: boolean;
  commercialId: number;
  previewCommercialData: CommercialDTO;
  loading: boolean;
  wizardState: string;
  wizardPreviewState: string;
  createCommercialClick: boolean;
  private hasRequirementDataChanged: boolean;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly api: API,
    public readonly modal: ModalService,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    private readonly bookingService: BookingService,
    public readonly translate: TranslateService,
    public readonly navService: NavigationService,
    private readonly toast: ToasterService,
    public readonly permissionService: PermissionService,
    public readonly commercialService: CommercialReservationService,
    private readonly queryParamService: QueryParamService
  ) {
    this.hasRequirementDataChanged = false;
    this.isREWEClient = false;
    this.minMaxPoints = undefined;
    this.clusters = [];
    this.form = new UntypedFormGroup(
      {
        Title: new UntypedFormControl('', [Validators.required]),
        Identifier: new UntypedFormControl(''),
        Description: new UntypedFormControl(''),
        From: new UntypedFormControl('', [Validators.required]),
        Till: new UntypedFormControl('', [Validators.required]),
        Duration: new UntypedFormControl('', [Validators.required]),
        Frequency: new UntypedFormControl('', [Validators.required]),
      },
      [this.util.endDateAfterStartDate]
    );

    this.clientId = undefined;
    this.campaignId = undefined;
    this.campaignData = null;
    this.commercialData = null;
    this.requirementData = null;
    this.useMotiveGroupForBookings = false;
    this.commercialId = undefined;
    this.previewCommercialData = undefined;
    this.loading = true;
    this.wizardState = 'Map';
    this.wizardPreviewState = 'locationMap';
    this.createCommercialClick = false;
  }

  get Title() {
    return this.form.get('Title');
  }

  get From() {
    return this.form.get('From');
  }

  get Till() {
    return this.form.get('Till');
  }

  get Duration() {
    return this.form.get('Duration');
  }

  get Frequency() {
    return this.form.get('Frequency');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Identifier() {
    return this.form.get('Identifier');
  }

  ngOnInit() {
    this.isREWEClient = this.util.getSeatbackCMSClient() === 'rewe';
    this.activatedRoute.params.subscribe(params => {
      this.clientId = params.clientID;
      this.campaignId = params.campaignID;
      this.commercialId = params.commercialID;
      this.refreshFromServer();
    });
    this.registerFormChangesListener();
  }

  nextStep(wizard: any, nextTab: any) {
    wizard.selectTab(nextTab);
  }

  getDurationFromMotive($event: any) {
    $event.stopPropagation();
    $event.preventDefault();
    this.modal
      .modal(
        {
          title: '',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'mediaexplorermodald',
        }
      )
      .then((medium: MediumFullDTO) => {
        if (medium) {
          if (medium.duration) {
            this.Duration.setValue(medium.duration);
            this.toast.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.durationSetSuccessfully'));
          } else {
            this.toast.toast(ToastType.WARNING, this.translate.instant('lang.globals.durationNotFound'));
          }
        } else {
          this.toast.toast(ToastType.WARNING, this.translate.instant('lang.globals.mediumNotFound'));
        }
      })
      .catch(() => {});
  }

  openInventoryView() {
    this.modal
      .modal(
        {
          title: '',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'inventorySelectionModal',
        }
      )
      .then((cull: PlayerCullDTO) => {
        if (cull) {
          this.setCull(cull);
        }
      })
      .catch(() => {});
  }

  sliceCull(e: any, type: string) {
    switch (type) {
      case 'TimeCull': {
        if (this.commercialData.timeCull) {
          this.commercialData.timeCull = null;
        }
        break;
      }
      case 'PlayerCull': {
        if (this.commercialData.playerCull) {
          this.commercialData.playerCull = null;
          this.refreshMapSettings();
        }
        break;
      }
      case 'GeoCull': {
        if (this.commercialData.geoCull) {
          this.commercialData.geoCull = null;
          this.refreshMapSettings();
        }
        break;
      }
    }
    this.previewCommercial();
    e.stopPropagation();
  }

  selectFilter(cull: CullDTO) {
    this.setCull(cull);
    this.changeWizardState('Map');
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  changeWizardPreviewState(state: string) {
    this.wizardPreviewState = state;
  }

  filterSubmitSuccess(cull: CullWithAtomsDTO) {
    if (!cull) {
      this.changeWizardState('Map');
    } else {
      switch (cull.type) {
        case 'TimeCull':
          this.selectFilter(cull as TimeCullDTO);
          break;
        case 'PlayerCull':
          this.selectFilter(cull as PlayerCullDTO);
          break;
        case 'GeoCull':
          this.selectFilter(cull as GeoCullDTO);
          break;
      }
    }
  }

  updateForm() {
    this.Title.setValue(this.commercialData.name);
    this.Identifier.setValue(this.commercialData.identifier);
    this.Description.setValue(this.commercialData.description);
    this.Duration.setValue(this.commercialData.duration);
    this.Frequency.setValue(this.commercialData.frequency);
    this.From.setValue(this.commercialData.from);
    this.Till.setValue(this.commercialData.till);
  }

  setCommercialData(data: CommercialFullDTO) {
    this.commercialData = { ...data }; // keep requirements from initial api request
    if (!this.commercialData.from) {
      this.commercialData.from = new SbLocalDateTime(DateTime.local());
    }
    if (!this.commercialData.till) {
      this.commercialData.till = new SbLocalDateTime(DateTime.local());
    }

    this.From.setValue(this.commercialData.from);
    this.Till.setValue(this.commercialData.till);
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .ds_cm_Campaign_retrieveByIdFrontendEnhanced(this.campaignId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.campaignData = data;
          if (!this.commercialId) {
            this.initNewCommercial();
          } else {
            this.loadCommercial();
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  private initNewCommercial() {
    this.commercialData = new CommercialFullDTO();
    if (this.commercialService.entry) {
      const startDate = DateTime.fromISO(this.commercialService.entry.startDate as unknown as string);
      this.commercialData.from = new SbLocalDateTime(
        DateTime.local(
          startDate.year,
          startDate.month,
          startDate.day,
          this.commercialService.entry.startTime?.value?.hour ?? 0,
          this.commercialService.entry.startTime?.value?.minute ?? 0,
          0
        )
      );
      const endDate = DateTime.fromISO(this.commercialService.entry.endDate as unknown as string);
      this.commercialData.till = new SbLocalDateTime(
        DateTime.local(
          endDate.year,
          endDate.month,
          endDate.day,
          this.commercialService.entry.endTime?.value?.hour ?? 23,
          this.commercialService.entry.endTime?.value?.minute ?? 59,
          59
        )
      );
      this.commercialData.name = this.commercialService.entry.name;
    } else {
      const qpFrom = this.queryParamService.getQueryParam('from');
      const qpTill = this.queryParamService.getQueryParam('till');
      const qpFromDateTime = qpFrom ? DateTime.fromISO(qpFrom) : DateTime.local();
      const qpTillDateTime = qpTill ? DateTime.fromISO(qpTill) : DateTime.local();
      this.commercialData.from = new SbLocalDateTime(qpFromDateTime.startOf('day'));
      this.commercialData.till = new SbLocalDateTime(qpTillDateTime.endOf('day'));
      this.commercialData.name = '';
    }
    const playerCullId = +this.queryParamService.getQueryParam('playerCullSet');
    const geoCullId = +this.queryParamService.getQueryParam('geoCullSet');
    const timeCullId = +this.queryParamService.getQueryParam('timeCullSet');
    const cullIds: number[] = [playerCullId, geoCullId, timeCullId].filter(Boolean);
    if (cullIds?.length > 0) {
      this.api
        .cs_Cull_retrieveAll({ id: cullIds })
        .pipe(take(1))
        .subscribe({
          next: culls => {
            this.commercialData.playerCull = (culls.find(cull => cull.id === playerCullId) as PlayerCullDTO) ?? null;
            this.commercialData.timeCull = (culls.find(cull => cull.id === timeCullId) as TimeCullDTO) ?? null;
            this.commercialData.geoCull = (culls.find(cull => cull.id === geoCullId) as GeoCullDTO) ?? null;
          },
        });
    }
    if (this.commercialService.playerCull) {
      this.commercialData.playerCull = this.commercialService.playerCull;
    }
    this.commercialData.active = true;
    this.commercialData.duration = this.isREWEClient ? 6 : 10;
    this.commercialData.frequency = 1;
    this.updateForm();
    this.previewCommercial();
    this.loading = false;
  }

  private loadCommercial() {
    this.loading = true;
    this.api
      .ds_cm_Commercial_retrieveById(this.commercialId)
      .pipe(
        switchMap((commercial: CommercialDTO) => {
          this.setCommercialData({ ...commercial, requirements: null });
          this.updateForm();
          this.previewCommercial();
          return this.api.ds_cm_Commercial_retrieveRequirements(this.commercialData.id);
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (requirements: RequirementFullDTO[]) => {
          this.commercialData.requirements = requirements;
          this.requirementData = requirements[0];
          if (this.queryParamService.getQueryParam('skipToBooking')) {
            this.wizard.selectTab(this.tab3);
          }
        },

        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  navigateBackToCommercialList() {
    try {
      this.modal.close('finishmodal');
    } catch (ex) {}
    this.util.returnToNavigation(this.util.RoutingID.commercials, { id: 'campaignID', value: this.campaignId });
  }

  navigateBackToRequirementsList() {
    this.navService.navigateToPoint(RoutingID.requirements, [
      {
        id: 'campaignID',
        value: this.campaignId,
      },
      {
        id: 'commercialID',
        value: this.commercialId,
      },
    ]);
  }

  createAndUpdateCommercial(completeAction: any, wizard?: SbWizardComponent, nextTab?: SbWizardTabComponent) {
    this.createCommercialClick = true;

    if (!this.commercialData.id) {
      this.api
        .ds_cm_Campaign_createCommercial(this.campaignData.id, this.commercialData)
        .pipe(take(1))
        .subscribe({
          next: (commercial: CommercialFullDTO) => {
            this.commercialService.resetService();

            this.setCommercialAndCreateRequirement(commercial)
              .then(() => {
                wizard.selectTab(nextTab, true);
                this.createCommercialClick = false;
              })
              .catch(() => {
                this.createCommercialClick = false;
              });
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Commercial_editCommercial(this.commercialData)
        .pipe(take(1))
        .subscribe({
          next: (commercial: CommercialDTO) => {
            if (!this.commercialData.requirements || this.commercialData.requirements?.length === 0) {
              // no requirementCount or count === 0 => create requirement

              this.setCommercialAndCreateRequirement(commercial).then(() => {
                wizard.selectTab(nextTab, true);
                this.createCommercialClick = false;
              });
            } else if (this.commercialData.requirements?.length === 1) {
              this.createCommercialClick = true;
              // one requirement: standard-case
              this.setCommercialData({ ...commercial, requirements: this.commercialData.requirements });
              this.api
                .ds_cm_Requirement_retrieveById(this.commercialData.requirements[0].id)
                .pipe(
                  take(1),
                  finalize(() => {
                    this.createCommercialClick = false;
                  })
                )
                .subscribe({
                  next: (requirement: RequirementFullDTO) => {
                    this.requirementData = requirement;
                    wizard.selectTab(nextTab, true);
                  },
                  error: error => {
                    this.errorHandler.errorHandler(error);
                  },
                });
            } else {
              // more than one requirement => redirect to requirement list
              // this.navigateBackToRequirementsList();
              wizard.selectTab(nextTab, true);
              this.createCommercialClick = false;
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            this.createCommercialClick = false;
          },
        });
    }
  }

  setCommercialAndCreateRequirement(commercial: CommercialDTO): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.setCommercialData({ ...commercial, requirements: null });
      this.bookingService.createRequirements(this.commercialData).then((createdRequirements: RequirementFullDTO[]) => {
        if (!createdRequirements || createdRequirements?.length < 1 || createdRequirements?.length > 1 || !createdRequirements[0]) {
          const err = new Error('No or too many requirements created');
          reject(err);
          throw err;
        }
        this.commercialData.requirements = createdRequirements;
        this.requirementData = createdRequirements[0];
        resolve();
      });
    });
  }

  setCull(cull: CullDTO) {
    switch (cull.type) {
      case 'TimeCull':
        this.commercialData.timeCull = cull as TimeCullDTO;
        if (!cull.id) {
          this.commercialData.timeCull = null;
        }
        break;
      case 'StateSpaceCull':
        this.commercialData.stateSpaceCull = cull as StateSpaceCullDTO;
        if (!cull.id) {
          this.commercialData.stateSpaceCull = null;
        }
        break;
      case 'GeoCull':
        this.commercialData.geoCull = cull as GeoCullDTO;
        if (!cull.id) {
          this.commercialData.geoCull = null;
        }
        break;
      case 'PlayerCull':
        this.commercialData.playerCull = cull as PlayerCullDTO;
        if (!cull.id) {
          this.commercialData.playerCull = null;
        }
        break;
    }
    this.previewCommercial();
  }

  previewCommercial() {
    if (!this.commercialId && !this.commercialData.id) {
      this.api
        .ds_cm_Campaign_createCommercialPreview(this.campaignData ? this.campaignData.id : null, this.commercialData)
        .pipe(take(1))
        .subscribe({
          next: data => {
            this.previewCommercialData = data;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Commercial_editCommercialPreview(this.commercialData)
        .pipe(take(1))
        .subscribe({
          next: data => {
            this.previewCommercialData = data;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
    this.refreshMap();
  }

  finish() {
    this.loading = true;
    iif(() => this.hasRequirementDataChanged, this.api.ds_cm_Requirement_editRequirement(this.requirementData), of({}))
      .pipe(
        switchMap(() => this.api.ds_cm_Booking_editBookings(this.requirementData.bookings)),
        take(1)
      )
      .subscribe({
        next: () => {
          this.modal
            .modal(
              {
                title: !this.commercialId
                  ? this.translate.instant('lang.commercialEdit.createdSuccessfully')
                  : this.translate.instant('lang.commercialEdit.editedSuccessfully'),
                modalOptions: { windowClass: 'M' },
              },
              {
                templateId: 'finishmodal',
              }
            )
            .then(() => {
              this.navigateBackToCommercialList();
            })
            .catch(() => {
              this.navigateBackToCommercialList();
            });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  updateBookings(bookings: BookingDTO[]) {
    this.requirementData.bookings = bookings;
  }

  showBookingsNotice(): boolean {
    for (const booking of this.requirementData.bookings) {
      if (booking.status === BookingStatus.VerificationRequested) {
        return true;
      }
    }
    return false;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  localDateTimeInputFrom(date: SbLocalDateTime) {
    this.From.setValue(date);
  }

  localDateTimeInputTill(date: SbLocalDateTime) {
    this.Till.setValue(date);
  }

  refreshMap(pClusterMapBounds?: MinMaxPoints) {
    if (pClusterMapBounds) {
      this.minMaxPoints = pClusterMapBounds;
    }
    this.refreshMapSettings();
  }

  refreshMapSettings() {
    this.api
      .ds_rm_Inventory_geoAggregationFrontendEnhanced(
        99,
        {
          filters: null,
          whitelistPolygons: null,
          blacklistPolygons: null,
          playerCulls: this.getPlayerCullArray(this.campaignData?.playerCull, this.commercialData?.playerCull),
          geoCulls: this.getGeoCullArray(this.campaignData?.geoCull, this.commercialData?.geoCull),
          descriptorId: null,
        },
        { min: this.minMaxPoints?.min, max: this.minMaxPoints?.max }
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (aggregation: InventoryGeoAggregationFrontendEnhancedDTO) => {
          this.clusters = aggregation.data;
        },
      });
  }

  getPlayerCullArray(campaignCull: PlayerCullDTO, commercialCull: PlayerCullDTO): PlayerCullDTO[] | null {
    const res: PlayerCullDTO[] = [];
    if (!campaignCull && !commercialCull) {
      return null;
    }
    if (campaignCull) {
      res.push(campaignCull);
    }
    if (commercialCull) {
      res.push(commercialCull);
    }
    return res;
  }

  getGeoCullArray(campaignCull: GeoCullDTO, commercialCull: GeoCullDTO): GeoCullDTO[] | null {
    const res: GeoCullDTO[] = [];
    if (!campaignCull && !commercialCull) {
      return null;
    }
    if (campaignCull) {
      res.push(campaignCull);
    }
    if (commercialCull) {
      res.push(commercialCull);
    }
    return res;
  }

  getNameFromMotive($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.modal
      .modal(
        {
          title: '',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'mediaexplorermodald',
        }
      )
      .then((medium: MediumFullDTO) => {
        if (medium) {
          if (medium.name) {
            this.Title.setValue(medium.name);
            this.toast.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.nameSetSuccessfully'));
          } else {
            this.toast.toast(ToastType.WARNING, this.translate.instant('lang.globals.nameNotFound'));
          }
        } else {
          this.toast.toast(ToastType.WARNING, this.translate.instant('lang.globals.mediumNotFound'));
        }
      })
      .catch(() => {});
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      this.commercialData.name = value;
    });
    this.Frequency.valueChanges.subscribe(value => {
      this.commercialData.frequency = value;
    });
    this.Duration.valueChanges.subscribe(value => {
      this.commercialData.duration = value;
    });
    this.Identifier.valueChanges.subscribe(value => {
      this.commercialData.identifier = value;
    });
    this.Description.valueChanges.subscribe(value => {
      this.commercialData.description = value;
    });
    this.From.valueChanges.subscribe(value => {
      let diff: Duration = null;
      if (this.commercialData?.till?.value && this.commercialData?.from?.value) {
        diff = this.commercialData.till.value.diff(this.commercialData.from.value);
      }
      this.commercialData.from = value;
      if (this.commercialData?.from?.value && this.commercialData?.till?.value) {
        this.commercialData.till = diff ? new SbLocalDateTime(this.commercialData.from.value.plus(diff)) : this.commercialData.from;
        setTimeout(() => {
          this.Till.setValue(this.commercialData.till);
        }, 1);
      }
    });
    this.Till.valueChanges.subscribe(value => {
      this.commercialData.till = value;
    });
  }

  requirementDataChanged($event: boolean) {
    this.hasRequirementDataChanged = $event;
  }
}
